// Type definitions for unist/unified ecosystem
type Node = {
  type: string;
  value?: string;
  children?: Node[];
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Plugin = () => (tree: Node) => Node;

// Simple implementation of visit function to avoid dependency issues
const _shouldVisitNode = (node: Node, type: string): boolean => {
  return node.type === type;
};

const _traverseChildren = (
  node: Node,
  type: string,
  visitor: (node: Node, index: number | null, parent: Node | null) => void
): void => {
  if (!node.children || !Array.isArray(node.children)) return;
  for (let i = 0; i < node.children.length; i++) {
    _traverseNode(node.children[i], i, node, type, visitor);
  }
};

const _traverseNode = (
  node: Node,
  index: number | null,
  parent: Node | null,
  type: string,
  visitor: (node: Node, index: number | null, parent: Node | null) => void
): void => {
  if (_shouldVisitNode(node, type)) {
    visitor(node, index, parent);
  }
  _traverseChildren(node, type, visitor);
};

function visit(
  tree: Node,
  type: string,
  visitor: (node: Node, index: number | null, parent: Node | null) => void
): void {
  _traverseNode(tree, null, null, type, visitor);
}

interface CitationMap {
  [key: string]: unknown;
}

interface CitationNode extends Node {
  type: string;
  value?: string;
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
  };
}

const _createTextNode = (value: string): CitationNode => ({
  type: 'text',
  value,
});

const _createCitationNode = (citationNumber: string): CitationNode => ({
  type: 'citation',
  value: citationNumber,
  data: {
    hName: 'span',
    hProperties: {
      'data-citation-number': citationNumber,
    },
  },
});

const _processCitationMatches = (value: string): CitationNode[] => {
  const citationRegex = /\[(\d+)\]/g;
  const newNodes: CitationNode[] = [];
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  while ((match = citationRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      newNodes.push(_createTextNode(value.slice(lastIndex, match.index)));
    }
    newNodes.push(_createCitationNode(match[1]));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < value.length) {
    newNodes.push(_createTextNode(value.slice(lastIndex)));
  }

  return newNodes;
};

const _replaceTextNodeWithCitations = (node: CitationNode, index: number, parent: Node): void => {
  if (index === null || !parent || typeof node.value !== 'string') return;

  const newNodes = _processCitationMatches(node.value);
  if (newNodes.length > 0 && parent.children) {
    parent.children.splice(index, 1, ...newNodes);
  }
};

export const remarkCitations = (citations?: CitationMap, isStreaming?: boolean): Plugin => {
  const transformer: Plugin = () => (tree: Node) => {
    visit(tree, 'text', (node: CitationNode, index: number | null, parent: Node | null) => {
      if (index === null || !parent) return;
      _replaceTextNodeWithCitations(node, index, parent);
    });
    return tree;
  };

  return transformer;
};
